import {type FeatureApiResponse} from '@growthbook/growthbook'

import {Features} from '#/analytics/features/types'

type FlagValue = boolean | number | Record<string, number>

/**
 * Sunnahsky sets every feature switch here instead of reading Bluesky's
 * GrowthBook settings from events.bsky.app, which answers any site and so let
 * Bluesky decide which gated features Sunnahsky members saw.
 *
 * The type covers upstream's whole `Features` list, so a switch an upstream
 * merge adds fails the typecheck until it has a value here. "Off" is not one
 * value: a switch named `...Disable` hides its feature when it is `true`.
 *
 * Values decided by the owner on 2026-09-24; see
 * social-app-upstream-merge-plan.md, "Before step 1", item 3.
 */
export const SUNNAHSKY_FLAGS: {readonly [K in Features]: FlagValue} = {
  // Bluesky staff tools
  [Features.IsBskyTeam]: false,
  [Features.DebugFeedContext]: false,
  // Hidden: these show outside accounts or send members' data to Bluesky
  [Features.ImportContactsOnboardingDisable]: true,
  [Features.ImportContactsSettingsDisable]: true,
  [Features.GroupChatsDisable]: true,
  [Features.PostThreadKnownLikersEnable]: false,
  [Features.PostFeedKnownLikersEnable]: false,
  // Hidden: it shows Bluesky's moderation actions and community guidelines
  [Features.ModerationInboxEnable]: false,
  /*
   * Allowed, for Strikers only: useLiveNowConfig hides it from Catchers and
   * the PDS rejects a Catcher's live-status record.
   */
  [Features.LiveNowBetaDisable]: false,
  [Features.PostGalleryEmbedEnable]: true,
  [Features.ComposerLanguageDetectionEnable]: false,
  [Features.FollowSortEnable]: false,
  [Features.OnboardingInterestsRequiredEnable]: false,
  [Features.CustomLogoJapanEnable]: false,
  [Features.AATest]: false,
  // Trending was removed from the fork; upstream's defaults, never read
  [Features.TrendingDiscoverValues]: {topics: 5, accounts: 15, videos: 30},
  [Features.TrendingExploreTopicsCountValue]: 5,
}

/**
 * Switches for features upstream doesn't gate. Code reads these directly;
 * they are not part of the GrowthBook payload.
 */
export const SUNNAHSKY_FORK_FLAGS: {
  readonly threadPostNumbering: boolean
} = {
  /*
   * Numbers the original poster's posts in a thread ("2/5"). Upstream shows
   * it always since removing `canonical_post_numbering:enable`; off by owner
   * decision, 2026-09-24.
   */
  threadPostNumbering: false,
}

/**
 * SUNNAHSKY_FLAGS as a GrowthBook payload: one feature per switch, each
 * with only a default value, so no targeting rule can change it.
 */
export function sunnahskyFeaturePayload(): FeatureApiResponse {
  return {
    features: Object.fromEntries(
      Object.entries(SUNNAHSKY_FLAGS).map(([key, value]) => [
        key,
        {defaultValue: value},
      ]),
    ),
  }
}
